import React, { useState, useEffect } from 'react';
import api from '../services/api';
import {
    UserCircle,
    Phone,
    Mail,
    Home,
    LogOut,
    Shield,
    Save,
    Camera
} from 'lucide-react';
import { cn } from '../lib/utils';

const Profile: React.FC = () => {
    const [profile, setProfile] = useState<any>(null);
    const [name, setName] = useState('');
    const [phone, setPhone] = useState('');
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        api.get('/profiles/me').then(res => {
            setProfile(res.data);
            setName(res.data.name);
            setPhone(res.data.phone_number || '');
        });
    }, []);

    const handleSave = async (e: React.FormEvent) => {
        e.preventDefault();
        setSaving(true);
        try {
            // Assume backend endpoint exists or patch logic is handled
            // await api.patch('/profiles/me', { name, phone_number: phone });
            alert('Profile updated');
        } catch (e) { alert('Update failed'); }
        setSaving(false);
    };

    if (!profile) return null;

    return (
        <div className="space-y-8 max-w-2xl mx-auto pb-10">
            <div>
                <h1 className="text-3xl font-bold text-slate-900">Your Profile</h1>
                <p className="text-slate-500 mt-1">Manage your account and unit details</p>
            </div>

            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
                <div className="bg-violet-600 h-32 relative">
                    <div className="absolute -bottom-10 left-8">
                        <div className="w-24 h-24 rounded-3xl bg-white border-4 border-white shadow-xl flex items-center justify-center text-violet-600 font-bold text-4xl overflow-hidden relative group">
                            {profile.name.charAt(0)}
                            <button className="absolute inset-0 bg-black/40 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                                <Camera size={24} />
                            </button>
                        </div>
                    </div>
                </div>

                <div className="pt-14 p-8">
                    <form onSubmit={handleSave} className="space-y-6">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div className="space-y-1">
                                <label className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">Full Name</label>
                                <div className="relative">
                                    <UserCircle className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                                    <input value={name} onChange={e => setName(e.target.value)} className="w-full bg-slate-50 border border-slate-200 rounded-xl py-2.5 pl-10 pr-4 text-sm focus:ring-2 focus:ring-violet-500 outline-none transition-all" />
                                </div>
                            </div>
                            <div className="space-y-1">
                                <label className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">Phone Number</label>
                                <div className="relative">
                                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                                    <input value={phone} onChange={e => setPhone(e.target.value)} className="w-full bg-slate-50 border border-slate-200 rounded-xl py-2.5 pl-10 pr-4 text-sm focus:ring-2 focus:ring-violet-500 outline-none transition-all" />
                                </div>
                            </div>
                            <div className="space-y-1">
                                <label className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">Email Address</label>
                                <div className="relative">
                                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                                    <input value={profile.email} disabled className="w-full bg-slate-50 border border-slate-200 rounded-xl py-2.5 pl-10 pr-4 text-sm text-slate-400 cursor-not-allowed" />
                                </div>
                            </div>
                            <div className="space-y-1">
                                <label className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">Role</label>
                                <div className="relative">
                                    <Shield className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                                    <input value={profile.role} disabled className="w-full bg-slate-50 border border-slate-200 rounded-xl py-2.5 pl-10 pr-4 text-sm text-slate-400 capitalize cursor-not-allowed" />
                                </div>
                            </div>
                        </div>

                        <div className="pt-6 border-t border-slate-100 space-y-4">
                            <h3 className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">Registered Units</h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                {profile.units?.map((u: any) => (
                                    <div key={u.id} className="flex items-center gap-3 p-3 bg-slate-50 rounded-xl border border-slate-200">
                                        <div className="w-8 h-8 rounded-lg bg-white border border-slate-200 flex items-center justify-center text-slate-500">
                                            <Home size={16} />
                                        </div>
                                        <div>
                                            <p className="text-sm font-bold text-slate-900">Unit {u.unit_number}</p>
                                            <p className="text-[10px] text-slate-500 font-medium">Block {u.block || 'N/A'} · Floor {u.floor || 'N/A'}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div className="pt-8 flex justify-end gap-3">
                            <button
                                type="button"
                                onClick={() => { localStorage.removeItem('token'); window.location.href = '/login'; }}
                                className="flex items-center gap-2 px-6 py-2.5 rounded-xl font-bold text-sm text-red-500 hover:bg-red-50 transition-all"
                            >
                                <LogOut size={18} />
                                Sign Out
                            </button>
                            <button type="submit" disabled={saving} className="flex items-center gap-2 px-8 py-2.5 bg-violet-600 text-white rounded-xl font-bold text-sm hover:bg-violet-700 shadow-lg shadow-violet-200 transition-all disabled:opacity-50">
                                <Save size={18} />
                                {saving ? 'Saving...' : 'Update Profile'}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default Profile;
